// D138 (§8.1) — the lesson runner.
//
// Sits between the sim and the objective panel. It owns the CHECKLIST (which sub-goals are done, how far
// the active one has got, how many attempts you have had) and hands `ui/` a plain view-model — the
// eslint boundary is real: `ui/` may not touch MatchState, exactly as `difficultyPicker` may not.
//
// Two rules carry the whole psychology the user asked for:
//   · ATTEMPTS ARE FREE, PROGRESS IS PERMANENT. The auto-reset re-applies the SCENARIO, never the
//     checklist. A completed sub-goal stays completed across every retry, and a setback zeroes the
//     ACTIVE chip only — never a check you have already earned.
//   · ONE SKILL PER STEP, each individually detectable and celebrated. Steps advance strictly in order,
//     and events on the ACTIVATION tick count for the newly-active step (a goal scored during the
//     dribble step does not silently pre-complete the score step, because that step was not active yet).

import type { LessonSpec, LessonStep } from '@fafi/scenarios';
import type { MatchState, SimEvent } from '@fafi/shared';

export interface LessonStepView {
  readonly id: string;
  /** Coaching copy with `{key:action}` already resolved against the live bindings. */
  readonly instruction: string;
  readonly done: boolean;
  readonly active: boolean;
  /** Progress toward a counted goal — `{ have, need }`, or null for a plain one. */
  readonly count: { readonly have: number; readonly need: number } | null;
}

export interface LessonView {
  readonly id: string;
  readonly title: string;
  readonly icon: string;
  readonly steps: readonly LessonStepView[];
  readonly bonus: { readonly instruction: string; readonly done: boolean } | null;
  readonly attempts: number;
  readonly complete: boolean;
  /** Transient coach line to show this frame (praise on a step completion), else null. */
  readonly praise: string | null;
}

/** Resolve `{key:action}` placeholders against the live bindings. Unknown/unbound ⇒ a readable fallback. */
export type KeyResolver = (action: string) => string;

const resolveCopy = (text: string, keyFor: KeyResolver): string =>
  text.replace(/\{key:([a-zA-Z]+)\}/g, (_m, action: string) => {
    const label = keyFor(action);
    return label === '' ? '—' : label;
  });

export class LessonRunner {
  private stepIndex = 0;
  private readonly doneSteps = new Set<string>();
  private memo: unknown = undefined;
  private have = 0;
  private bonusDone = false;
  private bonusMemo: unknown = undefined;
  private attemptCount = 1;
  private praiseLine: string | null = null;
  private completedAt: number | null = null;

  constructor(
    private readonly lesson: LessonSpec,
    private readonly keyFor: KeyResolver,
    /** D151: the entity the human drives, read LIVE — every detector is about him (see
     *  `LessonStep.goal.detect`). A getter rather than a value because auto-switching can move the human
     *  mid-lesson, and the objectives must follow whoever he is actually playing. */
    private readonly seatOf: () => number = () => 1,
  ) {}

  /** D153: the active step's `failOn` fired — the caller re-arms the board. Consumed by reading it. */
  private failedFlag = false;
  takeFailure(): boolean {
    const f = this.failedFlag;
    this.failedFlag = false;
    return f;
  }

  /** Feed one tick's events + the post-step state. Returns true when the lesson completed THIS tick. */
  onTick(events: readonly SimEvent[], state: MatchState): boolean {
    if (this.completedAt !== null) return false;
    const step = this.lesson.steps[this.stepIndex];
    if (step === undefined) return false;

    // the bonus runs alongside, from the first tick — it is optional, never gating (PT4-P17)
    const bonus = this.lesson.bonus;
    if (bonus !== undefined && !this.bonusDone) {
      const bp = bonus.goal.detect(events, state, this.bonusMemo, this.seatOf());
      this.bonusMemo = bp.memo;
      if (bp.done >= (bonus.goal.count ?? 1)) this.bonusDone = true;
    }

    const p = step.goal.detect(events, state, this.memo, this.seatOf());
    this.memo = p.memo;
    // a SETBACK zeroes the active chip, never a completed check
    this.have = Math.max(0, p.done);
    const need = step.goal.count ?? 1;
    if (this.have < need) {
      // D153: SUCCESS is evaluated first — a goal that also puts the ball out of play is a completed
      // rep, not a failed one. Only once this tick has failed to complete the step does failure count.
      if (step.failOn?.(events, state, this.seatOf()) === true) this.failedFlag = true;
      return false;
    }

    this.doneSteps.add(step.id);
    this.praiseLine = step.praise[(this.attemptCount - 1) % Math.max(1, step.praise.length)] ?? null;
    this.stepIndex += 1;
    this.memo = undefined; // a fresh step gets fresh scratch — memos never leak across the boundary
    this.have = 0;
    if (this.stepIndex >= this.lesson.steps.length) {
      this.completedAt = state.tick;
      return true;
    }
    return false;
  }

  /** The scenario re-armed (an outcome, or a manual restart). Progress is untouched — that is the rule. */
  noteAttempt(): void {
    this.attemptCount += 1;
  }

  /** Read and clear the transient coach line (it rides the banner overlay for a beat). */
  takePraise(): string | null {
    const p = this.praiseLine;
    this.praiseLine = null;
    return p;
  }

  get complete(): boolean {
    return this.completedAt !== null;
  }

  get attempts(): number {
    return this.attemptCount;
  }

  view(): LessonView {
    const steps: LessonStepView[] = this.lesson.steps.map((s: LessonStep, i) => ({
      id: s.id,
      instruction: resolveCopy(s.instruction, this.keyFor),
      done: this.doneSteps.has(s.id),
      active: i === this.stepIndex && this.completedAt === null,
      count: (s.goal.count ?? 1) > 1 ? { have: i === this.stepIndex ? this.have : this.doneSteps.has(s.id) ? (s.goal.count ?? 1) : 0, need: s.goal.count ?? 1 } : null,
    }));
    return {
      id: this.lesson.id,
      title: this.lesson.title,
      icon: this.lesson.icon,
      steps,
      bonus: this.lesson.bonus ? { instruction: resolveCopy(this.lesson.bonus.instruction, this.keyFor), done: this.bonusDone } : null,
      attempts: this.attemptCount,
      complete: this.completedAt !== null,
      praise: this.praiseLine,
    };
  }
}
